import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

const SWIPE_THRESHOLD = 50;

// Minimum finger movement (px) before a touch is treated as a swipe rather than a tap.
// Without this, #onTouchMove would call preventDefault() on the very first touchmove pixel —
// even the incidental jitter under a stationary tap — which suppresses the browser's
// synthesized click for that touch, making links/buttons in the active panel (e.g. slider
// CTAs) unclickable. Matches the DRAG_THRESHOLD pattern used by layered-slideshow.js and
// drag-zoom-wrapper.js for the same tap-vs-drag disambiguation.
const TAP_THRESHOLD = 10;

// Fallback unlock delay if --homepage-scroll-snap-duration (see base.css, the single
// source of truth for both the CSS transition and this timer) can't be read for some
// reason — kept as a safety net only, never as the value actually used in practice.
const FALLBACK_TRANSITION_MS = 600;

// A page turn needs this much *recent* scrolling behind it. The measure is a leaky
// integrator: each event adds its |deltaY| to a running total that decays by half every
// WHEEL_DISTANCE_HALF_LIFE_MS, so sustained scrolling builds it up while a dying inertia
// tail fades faster than it contributes.
//
// Both halves are load-bearing. Without the distance, one physical stroke turned two
// pages: recorded gestures on this store run a median of ~850ms, well past the transition,
// so the lock expires mid-stroke and the rest of the same stroke pages again. Without the
// decay — a plain total, reset when the lock lifts — a stroke that *began* during the lock
// lost its strongest part and could never reach the threshold, silently costing a page
// turn. Decay also means the total keeps accruing while locked, so such a stroke still
// counts, while the tail preceding it does not.
//
// Values sit in the middle of a plateau measured against recorded gestures: 90-120px over
// a 90-135ms half-life all score identically, so neither is a knife edge.
//
// This is a distance rule, not gesture detection: the shopper must scroll far enough,
// recently enough, the way a native scroll-snap container behaves. Every attempt to
// instead infer where a gesture *ended* is listed in docs/homepage-scroll-snap.md — each
// one shipped a different regression.
const WHEEL_DISTANCE_THRESHOLD = 100;
const WHEEL_DISTANCE_HALF_LIFE_MS = 120;

// Exactly one page turn per transition, and no attempt to classify the input beyond that.
// Wheel events carry no gesture boundaries, so every heuristic that tried to infer them
// from deltas and timing misfired on real hardware: decay-vs-peak swallowed a gentle
// stroke that followed a hard one (four strokes, one page), decay-vs-previous-event reset
// on the ordinary oscillation inside a single drag and never fired at all. Replaying
// recorded gestures, this rule beat both. The one behaviour it accepts is that an inertia
// tail outlasting the transition turns a second page - bounded, predictable, and far less
// bad than a scroll that does nothing.

// The lock runs for the *whole* transition, deliberately. Releasing early (this was once
// 55% of the duration, to kill a perceived beat before the next scroll was accepted) lets
// the next page turn start while the previous one is still animating: a real gesture trace
// showed 49% of turns beginning 339-587ms apart against a 600ms transition, so two panels
// were sliding at once for a quarter of every step — which is what "one scroll moves two
// sections" actually was. Any beat left over belongs to the easing curve, not here: fix it
// by making the curve finish sooner, never by unlocking before it does.

// Pure safety net: the unlock above always fires first, so this only matters if that timer
// is somehow never delivered. It is not a cadence control — an earlier attempt to make it
// one (shortening it so sustained scrolling would not stall) is what let a long inertia
// tail resume paging after it expired.
const HARD_UNLOCK_GRACE_MS = 125;

/**
 * Reads --homepage-scroll-snap-duration off :root (see base.css) so the JS unlock timer
 * can't drift out of sync with the CSS transition it's timed against — one declared
 * value instead of two hand-maintained copies.
 *
 * @returns {number}
 */
function readTransitionMs() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--homepage-scroll-snap-duration').trim();
  const parsed = parseFloat(raw);

  if (!Number.isFinite(parsed) || parsed <= 0) return FALLBACK_TRANSITION_MS;

  // The unit cannot be assumed. getPropertyValue returns a custom property's token
  // verbatim, and Shopify's asset pipeline minifies base.css — `600ms` there is served as
  // `.6s`, while `625ms` (no shorter as seconds) is served unchanged. `shopify theme dev`
  // serves it unminified either way, so reading this as milliseconds worked locally and
  // silently made every lock 0.6ms long on the deployed theme, letting one gesture page
  // through every section.
  const ms = raw.endsWith('s') && !raw.endsWith('ms') ? parsed * 1000 : parsed;

  // Anything this short can only be another unit the pipeline surprised us with: it would
  // disable the input lock rather than shorten it, so fail to the constant instead.
  return ms >= 50 ? ms : FALLBACK_TRANSITION_MS;
}

/**
 * The colour a panel paints behind the header, or null when it paints none of its own.
 *
 * Sections put their colour scheme's fill on a `.section-background` child rather than on
 * the section box itself (which stays the page default, white), so that child is what a
 * visitor actually sees under the header — reading the panel's own background-color
 * instead reports white for every panel and defeats the point. The panel itself is the
 * fallback for panels built without one, the footer being the live case here.
 *
 * Document order rather than geometry: these boxes stack in source order, so the first is
 * the one at the panel's top edge, which is the strip the header covers. The footer, which
 * holds two sections, is the only panel on this page with more than one.
 *
 * @param {HTMLElement} panel
 * @returns {string | null}
 */
function panelBackgroundColor(panel) {
  return inkedBackgroundColor(panel.querySelector('.section-background')) ?? inkedBackgroundColor(panel);
}

/**
 * @param {Element | null} element
 * @returns {string | null} Its computed background-color, or null when that is fully
 *   transparent — `rgba(r, g, b, 0)`, `color(srgb r g b / 0)` or the keyword itself,
 *   depending on how the engine serialises it.
 */
function inkedBackgroundColor(element) {
  if (!element) return null;

  const color = getComputedStyle(element).backgroundColor.trim();

  return color && color !== 'transparent' && !/[,/]\s*0\s*\)$/.test(color) ? color : null;
}

/**
 * Full-page "cover" pagination for the home page — each section fills the viewport and
 * the next section slides up over the current one, similar to ysl.com.
 *
 * The element itself is a tall spacer (see base.css) so the page can scroll through it
 * natively; a sticky inner viewport pins to the top of the screen while that happens.
 * While pinned, wheel/touch/keyboard input is hijacked to step between panels one at a
 * time instead of scrolling. At the first/last panel, input is released so the native
 * scroll can continue past the component (e.g. into the footer).
 */
class HomepageScrollSnap extends Component {
  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLElement[]} */
  #panels = [];

  #activeIndex = 0;
  #locked = false;

  /** Decaying |deltaY| total since the last page turn, its direction, and when it last grew. */
  #scrollSinceTurn = 0;
  #scrollDirection = 0;
  #scrollAt = null;
  #transitionMs = FALLBACK_TRANSITION_MS;

  /** @type {number | undefined} */
  #unlockTimer = undefined;

  /** @type {number | undefined} */
  #hardUnlockTimer = undefined;


  /** @type {number | null} */
  #touchStartY = null;

  /**
   * Tracks every section currently split into standalone mobile panels, keyed by the
   * section's shell element, so a later breakpoint crossing can reverse each cleanly.
   * Empty whenever every section is in its normal, merged-into-one-section state
   * (always true on desktop; also true on mobile before the first split runs).
   *
   * @type {Map<HTMLElement, { tiles: HTMLElement[], contentWrapper: Element, addedClasses: Map<HTMLElement, string[]> }>}
   */
  #mobileSplits = new Map();

  connectedCallback() {
    super.connectedCallback();

    const viewport = this.querySelector(':scope > .homepage-scroll-snap__viewport');
    if (!(viewport instanceof HTMLElement)) return;

    const panels = this.#buildPanels(viewport);
    if (panels.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Hands the document scrollbar back — base.css hides it from first paint, ahead of
      // this decision, so it doesn't vanish (and shift the page sideways) at init.
      this.setAttribute('data-inactive', '');
      return;
    }

    this.#viewport = viewport;
    this.#transitionMs = readTransitionMs();

    // Only reparents once activation is already decided — see #splitMobileTileSections.
    this.#splitMobileTileSections(panels);
    this.#panels = panels;

    const landingTarget = this.#landingTarget();
    const landingIndex = landingTarget ? this.#panels.findIndex((panel) => panel.contains(landingTarget)) : -1;
    if (landingIndex !== -1) this.#activeIndex = landingIndex;

    this.#render(true);
    this.setAttribute('data-initialized', 'true');

    // Paging never moves real scroll (see #resyncDocumentScroll), so activating the panel
    // above only guarantees the *panel* is on screen, not that the target is — a panel
    // taller than the viewport (see `overflow: hidden auto` in base.css) can still need
    // its own internal scroll to bring the target into view, the same way ordinary
    // in-flow anchor scrolling would have. `instant`, not the smooth behavior
    // `assets/base.css` sets on scroll containers by default, so this can't be caught
    // mid-animation by a page turn the way a native scroll correction can (see
    // #resyncDocumentScroll). Skipped when the hash names something outside every panel
    // (e.g. an in-page nav anchor elsewhere in the header) - that already resolves
    // natively and isn't this pager's concern.
    if (landingIndex !== -1 && landingTarget) landingTarget.scrollIntoView({ block: 'start', behavior: 'instant' });

    // Listeners are attached to `document`, not `this`, because the footer panel is
    // rendered outside this element's subtree (see base.css) — once it's the active,
    // fixed-position panel covering the screen, events targeting it would never bubble
    // up to a listener scoped to `this`.
    document.addEventListener('wheel', this.#onWheel, { passive: false });
    document.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    document.addEventListener('touchmove', this.#onTouchMove, { passive: false });
    document.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    document.addEventListener('keydown', this.#onKeydown);

    // The tile split only depends on breakpoint, not on anything that changes with
    // ordinary resizing — `mediaQueryLarge`'s own `change` event (rather than a raw
    // `resize` listener) already only fires on an actual crossing, so no separate
    // debouncing is needed here.
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('wheel', this.#onWheel);
    document.removeEventListener('touchstart', this.#onTouchStart);
    document.removeEventListener('touchmove', this.#onTouchMove);
    document.removeEventListener('touchend', this.#onTouchEnd);
    document.removeEventListener('keydown', this.#onKeydown);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    clearTimeout(this.#unlockTimer);
    clearTimeout(this.#hardUnlockTimer);
  }

  /**
   * The element the page should open on, if the URL landed here carrying a fragment
   * identifier naming one — most commonly the footer newsletter form, landed on after a
   * POST-redirect-GET round trip: `{% form %}` with an `id:` (see
   * blocks/email-signup.liquid) renders `action="/contact#<id>"`, and a browser carries
   * that fragment forward onto the final redirected URL even though Shopify's own
   * redirect response names no fragment of its own (confirmed against a throwaway
   * redirecting server here, since a real submission can't be driven from this
   * environment — see AGENTS.md).
   *
   * On an ordinary page this fragment already resolves natively - the browser's own
   * anchor-jump finds the (normally in-flow) element and scrolls to it. On this page it
   * can't: panels other than the active one are `inert`/transformed off-screen before the
   * jump would land, and #resyncDocumentScroll() would undo a document scroll that
   * somehow reached one anyway. Recognizing the landing state up front and opening
   * directly on the panel that holds it works *with* the pager instead of fighting it -
   * see connectedCallback.
   *
   * @returns {HTMLElement | null}
   */
  #landingTarget() {
    const hash = location.hash.slice(1);
    if (!hash) return null;

    try {
      return document.getElementById(decodeURIComponent(hash));
    } catch {
      // decodeURIComponent throws on a malformed escape sequence; fall back to the raw
      // (already-unescaped-by-the-browser-in-most-cases) hash rather than giving up.
      return document.getElementById(hash);
    }
  }

  /**
   * Builds the raw panel list straight from the current DOM shape: every top-level
   * section plus the footer. Used both on first load and to rebuild from scratch after
   * a breakpoint crossing. The `!el.hidden` check skips the jackets/rtw shell while it's
   * standing in for its two split-out tiles (see #splitMobileTileSection) — at every
   * other time nothing in here is hidden.
   *
   * @param {HTMLElement} viewport
   * @returns {HTMLElement[]}
   */
  #buildPanels(viewport) {
    const panels = /** @type {HTMLElement[]} */ (
      Array.from(viewport.children).filter((el) => el.classList.contains('shopify-section') && !el.hidden)
    );

    // The footer stays in its normal document position (see base.css) but is treated as
    // the final panel here so it covers the last section like any other.
    const footerPanel = document.querySelector('footer[data-homepage-scroll-snap-panel]');
    if (footerPanel instanceof HTMLElement) panels.push(footerPanel);

    return panels;
  }

  /**
   * Re-derives the panel list whenever a resize crosses the mobile/desktop breakpoint —
   * ordinary resizing that stays on the same side of it never touches the DOM or resets
   * scroll state. Reverses every tile split first if any were active (so #buildPanels
   * sees the plain, unsplit DOM shape), then re-splits whatever qualifies if the new
   * breakpoint is mobile.
   *
   * The panel the visitor was on carries over by element identity where possible, so
   * crossing the breakpoint mid-browse doesn't just snap back to the top; the one case
   * where the *element* itself stops existing as a panel — it was a full-height-tile
   * section's shell and just split into two tiles, or vice versa — is resolved to that
   * section's new equivalent instead.
   */
  #onBreakpointChange = () => {
    const viewport = this.#viewport;
    if (!viewport) return;

    const previousActivePanel = this.#panels[this.#activeIndex];
    const previousActiveIndex = this.#activeIndex;

    // Found before merging (which clears `#mobileSplits`) — works whether the visitor's
    // panel was currently the (desktop) shell itself or one of its (mobile) split tiles.
    const activeShell = this.#findFullHeightTileShells(viewport).find(
      (shell) => previousActivePanel === shell || this.#mobileSplits.get(shell)?.tiles.includes(previousActivePanel)
    );

    this.#mergeMobileTileSections();

    const panels = this.#buildPanels(viewport);
    this.#splitMobileTileSections(panels);

    let activeIndex = -1;
    if (activeShell) {
      const landingPanel = this.#mobileSplits.get(activeShell)?.tiles[0] ?? activeShell;
      activeIndex = panels.indexOf(landingPanel);
    }
    if (activeIndex === -1) activeIndex = panels.indexOf(previousActivePanel);
    if (activeIndex === -1) activeIndex = Math.min(previousActiveIndex, panels.length - 1);

    this.#panels = panels;
    this.#activeIndex = activeIndex;
    this.#locked = false;
    clearTimeout(this.#unlockTimer);
    clearTimeout(this.#hardUnlockTimer);
    this.#render(true);
  };

  /**
   * On mobile, sections built from two side-by-side tiles with the "full height tiles
   * on mobile" section setting on (e.g. "Light Weight Jackets" + "Ready To Wear",
   * "Handbags" + "Accessories") already render each tile at full viewport height there
   * — but stacked *inside* one over-tall panel, so reaching the second tile meant
   * scrolling within that panel rather than swiping to it like every other section.
   * Promoting the two tiles to be panels in their own right gives each its own swipe
   * stop, consistent with the rest of the page.
   *
   * Reparents the tiles out of their section to become direct children of `viewport` —
   * simpler and far less error-prone than teaching the panel system to nest, since the
   * tiles then get full-panel sizing/positioning (and paging/aria/inert handling) for
   * free from the exact same rules and #go()/#render() logic every other panel already
   * uses, just by also carrying the section wrapper's own classes (`shopify-section`,
   * `section-wrapper`, etc.).
   *
   * Only called once activation is already decided (`panels.length >= 2`, motion isn't
   * reduced) — reparenting only makes sense once the surrounding
   * `[data-initialized]`-scoped CSS that positions/sizes panels is actually going to
   * apply; doing it unconditionally would leave orphaned, unstyled tiles behind on a
   * page load where snap scroll never activates.
   *
   * Desktop is untouched: tiles stay exactly where they are, laid out side by side by
   * the section as today.
   *
   * Records what it did on `#mobileSplits` so a later breakpoint crossing can reverse it
   * (see #mergeMobileTileSections) instead of leaving stale panels/DOM behind.
   *
   * @param {HTMLElement[]} panels - Mutated in place: each qualifying section's single
   *   entry is replaced with its two tiles, in document order.
   */
  #splitMobileTileSections(panels) {
    const viewport = this.#viewport;
    if (!viewport || !isMobileBreakpoint()) return;

    for (const shell of this.#findFullHeightTileShells(viewport)) {
      if (this.#mobileSplits.has(shell)) continue;

      const index = panels.indexOf(shell);
      if (index === -1) continue;

      const contentWrapper = shell.querySelector('.section-content-wrapper');
      if (!contentWrapper) continue;

      const tiles = /** @type {HTMLElement[]} */ (
        Array.from(contentWrapper.querySelectorAll(':scope > .group-block')).filter(
          (el) => el instanceof HTMLElement
        )
      );
      if (tiles.length !== 2) continue;

      const shellClasses = Array.from(shell.classList);
      const addedClasses = new Map();

      tiles.forEach((tile) => {
        const existing = new Set(tile.classList);
        addedClasses.set(
          tile,
          shellClasses.filter((className) => !existing.has(className))
        );
        tile.classList.add(...shellClasses);
        viewport.insertBefore(tile, shell);
      });

      // Left in the DOM (rather than removed) so the section id stays available to the
      // theme editor — `hidden` takes it out of layout now that it has no content of
      // its own left to show. It's still excluded from `#panels` (see the splice
      // below), so it never gets paged to; it just sits inert, permanently covered by
      // the tiles that used to be inside it and every panel's own explicit, higher
      // z-index.
      shell.hidden = true;

      panels.splice(index, 1, ...tiles);

      this.#mobileSplits.set(shell, { tiles, contentWrapper, addedClasses });
    }
  }

  /**
   * Reverses #splitMobileTileSections: moves every split section's tiles back inside
   * their section and un-hides it. Also strips the inline panel state (transform,
   * z-index, aria-hidden, inert) that #render() had set on the tiles while they were
   * standing in as panels — none of that means anything once they're back to being
   * ordinary children of the section, and leaving it behind would render them wrongly
   * (or invisibly) on desktop.
   *
   * Only called from #onBreakpointChange.
   */
  #mergeMobileTileSections() {
    for (const [shell, { tiles, contentWrapper, addedClasses }] of this.#mobileSplits) {
      tiles.forEach((tile) => {
        tile.classList.remove(...(addedClasses.get(tile) ?? []));
        tile.style.transform = '';
        tile.style.zIndex = '';
        tile.removeAttribute('aria-hidden');
        tile.removeAttribute('inert');
        contentWrapper.appendChild(tile);
      });
      shell.hidden = false;
    }

    this.#mobileSplits.clear();
  }

  /**
   * Finds every top-level section wrapper with the "full height tiles on mobile"
   * setting on — the theme's own marker (see snippets/section.liquid) for exactly the
   * side-by-side-tiles layout #splitMobileTileSections handles, so any section
   * merchants configure this way (now or later) is picked up without hardcoding
   * specific section ids. Works regardless of whether a section is currently split (in
   * which case its shell is still in the DOM, just `hidden` — the marker lives on an
   * element that's never moved, only the tiles are).
   *
   * @param {HTMLElement} viewport
   * @returns {HTMLElement[]}
   */
  #findFullHeightTileShells(viewport) {
    return /** @type {HTMLElement[]} */ (
      Array.from(viewport.children).filter(
        (el) =>
          el instanceof HTMLElement &&
          el.classList.contains('shopify-section') &&
          el.querySelector('.section--full-height-tiles-mobile')
      )
    );
  }

  /**
   * Whether this input belongs to something other than the pager and must be left
   * entirely alone — no preventDefault(), no paging.
   *
   * The input listeners are bound to `document` (see connectedCallback), so they also
   * see gestures aimed at overlays layered above the page: the cart drawer, the
   * search/quick-add modals, the menu drawer, popovers, app-injected banners. Without
   * this, scrolling over an open drawer paged the background instead of scrolling the
   * drawer, because the wheel never reached the drawer's own scroller.
   *
   * @param {EventTarget | null} target
   */
  #shouldReleaseInput(target) {
    // A modal dialog covers the page with its own backdrop, so paging behind one is
    // never right regardless of where the gesture landed — including on the backdrop,
    // which hit-tests to the dialog rather than to a panel. Covers every
    // DialogComponent drawer/modal plus the hand-rolled showModal() overlays.
    // `:modal` rather than `[open]` because product hotspots and the size guide open
    // *non-modal* dialogs with .show(), which shouldn't gate the pager.
    //
    // The cart drawer is the one non-modal dialog that must still gate it: it
    // demotes itself to .show() while a third-party popup covers it (see
    // assets/cart-drawer.js) and is very much still covering the page then, so
    // it is matched explicitly rather than by `:modal`.
    if (document.querySelector('dialog:modal, .cart-drawer__dialog[open]')) return true;

    // The theme's own "page scrolling is locked" flag, set for any `<details
    // scroll-lock>` (see dialog.js) — today that's the header's menu drawer.
    if (document.documentElement.hasAttribute('scroll-lock')) return true;

    if (!(target instanceof Element)) return false;

    // Non-modal top-layer content: popovers (region switcher, sort dropdown, footer
    // policy list, volume pricing). Checked by target because the footer policy popover
    // lives inside the footer panel, so the panel test below wouldn't catch it.
    if (target.closest('[popover]:popover-open')) return true;

    // Last net, for overlays that are neither a dialog nor a popover (e.g. a consent or
    // geolocation drawer injected by an app embed): input aimed outside every panel, at
    // something with scrolling of its own. Gated on "scrollable" so ordinary non-panel
    // chrome — chiefly the header, which overlaps the first panel when transparent —
    // still pages as it does today.
    return !this.#panels.some((panel) => panel.contains(target)) && this.#hasScrollableAncestor(target);
  }

  /**
   * @param {Element} target
   */
  #hasScrollableAncestor(target) {
    for (let el = /** @type {Element | null} */ (target); el; el = el.parentElement) {
      if (el === document.body || el === document.documentElement) return false;
      const { overflowY } = getComputedStyle(el);
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) return true;
    }
    return false;
  }

  #isPinned() {
    const rect = this.getBoundingClientRect();

    // When the header is transparent, the whole component is pulled up to sit behind it
    // (see base.css), so this element's own top lands at 0. Otherwise it sits at its
    // natural position below the full header stack — --header-group-height alone
    // excludes the sticky header's own height (see base.css), so both are summed here.
    // (Read separately, not via the calc()-based --homepage-scroll-snap-header-offset
    // custom property in base.css: getPropertyValue on a calc() custom property returns
    // its literal unresolved expression string, not a computed pixel number.)
    const bodyStyle = getComputedStyle(document.body);
    const headerOffset = document.querySelector('.header[transparent]')
      ? 0
      : (parseFloat(bodyStyle.getPropertyValue('--header-group-height')) || 0) +
        (parseFloat(bodyStyle.getPropertyValue('--header-height')) || 0);

    return rect.top <= headerOffset + 1 && rect.bottom >= window.innerHeight - 1;
  }

  /**
   * @param {boolean} [skipTransition] - Suppresses the CSS transition for this render.
   *   Needed on first render: the footer panel goes from normal document flow to
   *   `position: fixed` + an off-screen transform in the same style recalculation, which
   *   browsers still animate as a transition (from an implicit "no transform" starting
   *   value), producing a visible slide-down flash on page load.
   */
  #render(skipTransition) {
    this.#panels.forEach((panel, index) => {
      if (skipTransition) panel.style.transition = 'none';

      // The footer panel lives outside the viewport (see base.css) as a separate
      // `position: fixed` element, so its z-index competes directly with the header's
      // own layer (--layer-sticky: 8) rather than being scoped by the viewport's
      // `isolation: isolate`. A small fixed value keeps it above the (isolated) regular
      // panels while always staying below the header, regardless of panel count.
      panel.style.zIndex = panel.tagName === 'FOOTER' ? '1' : String(index + 1);
      panel.style.transform = index <= this.#activeIndex ? 'translateY(0)' : 'translateY(100%)';
      panel.setAttribute('aria-hidden', index === this.#activeIndex ? 'false' : 'true');
      panel.toggleAttribute('inert', index !== this.#activeIndex);

      if (skipTransition) {
        // Force the browser to commit the transition-less state before restoring the
        // CSS-driven transition for subsequent (real) panel changes.
        void panel.offsetHeight;
        panel.style.transition = '';
      }
    });

    this.#syncHeaderOpacity();
    this.#resyncDocumentScroll();
  }

  /**
   * Returns the document to its scroll origin whenever this component is pinned.
   *
   * Paging never moves real scroll — panels are transforms, and `scrollTop` stays 0 for the
   * whole run — so a document sitting at a non-zero offset while pinned is always drift, not
   * a position the visitor chose. It is also self-sustaining: the pin covers the spacer's
   * entire length, so #isPinned() still holds at the new offset and the input handlers keep
   * consuming events to page panels, leaving nothing to scroll the document back. The visible
   * symptom is the announcement bar — in normal flow above the sticky header, so the only part
   * of the header stack real scroll can move — stranded off screen for the rest of the visit.
   *
   * The handlers above stop the drift this theme creates itself; this is the recovery path for
   * scroll that arrives another way (a scroll-into-view, a restored position, a browser that
   * chains a gesture to the document where Chrome does not).
   */
  #resyncDocumentScroll() {
    const scroller = document.scrollingElement;

    if (!scroller || scroller.scrollTop === 0 || !this.#isPinned()) return;

    // `behavior: 'instant'` is load-bearing: the theme sets `scroll-behavior: smooth` on the
    // root, so assigning scrollTop here animates instead of snapping — a correction that is
    // both visible as a long scroll of the whole document and still in flight on the next
    // page turn, which then lands somewhere between the two positions rather than at 0.
    scroller.scrollTo({ top: 0, behavior: 'instant' });
  }

  /**
   * Drives the transparent header's opaque/transparent switch (and, through the same
   * attribute, its white-vs-dark text - see the `[data-sticky-state]` rules in
   * sections/header.liquid) off whichever panel is actually active, in both directions.
   *
   * This replaces header.js's own scroll-position-based tracking for this page (see
   * `#isHomepagePaneDriven` in header.js): on this layout the header stays pinned to its
   * natural top-of-document position while panels page via transform, so real scroll
   * position doesn't reflect which panel is behind the header at all.
   *
   * A panel counts as "solid" - wanting an opaque header - unless it carries one of the
   * same full-bleed-image markers base.css already uses to decide whether that panel sits
   * behind the transparent header instead of clearing it (`.hero-wrapper`,
   * `.container-background-image`, `.section-wrapper`, or the editorial-focus carousel).
   * Same markers, same answer, so the opacity switch here can never disagree with the
   * layout those rules already produce. The footer is never one of these, so it always
   * reads as solid.
   *
   * A solid panel also hands the header its own background colour, through
   * --header-panel-background (see sections/header.liquid). The opaque header otherwise
   * fills with the header section's own colour scheme, which is white - correct on every
   * other template, where it is also the page's colour, but on this page the panel behind
   * it can be any scheme, and a white bar above a panel that isn't white reads as a seam
   * across the top of the page (`home-leather-edit` is color-scheme-3, the live case).
   */
  #syncHeaderOpacity() {
    const header = /** @type {HTMLElement | null} */ (
      document.querySelector('.header[transparent="not-sticky"]')
    );
    if (!header) return;

    const panel = this.#panels[this.#activeIndex];
    if (!panel) return;

    const isFullBleed =
      panel.tagName !== 'FOOTER' &&
      (panel.classList.contains('hero-wrapper') ||
        panel.classList.contains('container-background-image') ||
        panel.classList.contains('section-wrapper') ||
        !!panel.querySelector('.section-carousel--editorial-focus'));

    header.dataset.stickyState = isFullBleed ? 'inactive' : 'active';

    // Set together with the state, not after the panel lands: a turn's incoming panel only
    // reaches the header strip at the very end of its transition, so for most of the turn
    // the fill is covering the *outgoing* panel. That is what it is already for - it is why
    // the opaque state can flip up front at all - and it is as true of the panel's colour as
    // of the white it replaces.
    const background = isFullBleed ? null : panelBackgroundColor(panel);

    if (background) {
      header.style.setProperty('--header-panel-background', background);
    } else {
      header.style.removeProperty('--header-panel-background');
    }
  }

  /**
   * Attempts to move by `direction` (1 or -1) panels.
   *
   * @param {number} direction
   * @returns {boolean} Whether the input should be consumed (blocking native scroll).
   */
  #go(direction) {
    if (this.#locked) return true;

    const next = this.#activeIndex + direction;
    if (next < 0 || next > this.#panels.length - 1) return false;

    this.#activeIndex = next;
    this.#locked = true;
    this.#scrollSinceTurn = 0;
    this.#render();

    const activePanel = this.#panels[next];
    if (activePanel) activePanel.scrollTop = 0;

    this.#scheduleUnlock(this.#transitionMs);

    clearTimeout(this.#hardUnlockTimer);
    this.#hardUnlockTimer = setTimeout(() => {
      this.#locked = false;
      this.#scrollSinceTurn = 0;
    }, this.#transitionMs + HARD_UNLOCK_GRACE_MS);

    return true;
  }

  /**
   * (Re)schedules the unlock that fires once the transition has visually resolved, without
   * touching the hard-unlock safety net.
   *
   * @param {number} delay
   */
  #scheduleUnlock(delay) {
    clearTimeout(this.#unlockTimer);
    this.#unlockTimer = setTimeout(() => {
      this.#locked = false;
      this.#scrollSinceTurn = 0;
    }, delay);
  }

  /**
   * Whether the active panel has its own overflow left to scroll through in `direction`
   * before paging should take over. Panels taller than the viewport (see
   * `overflow: hidden auto` in base.css) would otherwise have their bottom permanently
   * clipped, since paging normally consumes every wheel/touch/key event itself.
   *
   * @param {number} direction
   */
  #activePanelCanScroll(direction) {
    const panel = this.#panels[this.#activeIndex];
    if (!panel) return false;

    // Most sections end up with some incidental trailing overflow (bottom padding,
    // collapsed margins, rounding) that isn't meaningful additional content. Ignore
    // overflow under this fraction of the panel's own height — otherwise a scroll
    // gesture gets "absorbed" nudging that sliver into view before the next section can
    // advance, instead of paging immediately. Scaled to the panel's height (not a fixed
    // px value) so it behaves consistently across viewport sizes.
    const overflow = panel.scrollHeight - panel.clientHeight;
    const tolerance = Math.max(24, panel.clientHeight * 0.12);
    if (overflow <= tolerance) return false;

    return direction > 0
      ? panel.scrollTop + panel.clientHeight < panel.scrollHeight - 1
      : panel.scrollTop > 1;
  }

  /**
   * Adds this event to the decaying scroll total. Reversing direction starts over rather
   * than crediting an upward turn with distance scrolled downward.
   *
   * @param {number} direction
   * @param {number} magnitude
   */
  #accumulate(direction, magnitude) {
    const now = performance.now();

    if (direction !== this.#scrollDirection) {
      this.#scrollDirection = direction;
      this.#scrollSinceTurn = 0;
    } else if (this.#scrollAt !== null) {
      this.#scrollSinceTurn *= Math.pow(0.5, (now - this.#scrollAt) / WHEEL_DISTANCE_HALF_LIFE_MS);
    }

    this.#scrollAt = now;
    this.#scrollSinceTurn += magnitude;
  }

  #onWheel = (/** @type {WheelEvent} */ event) => {
    if (this.#shouldReleaseInput(event.target) || !this.#isPinned()) return;

    const direction = event.deltaY > 0 ? 1 : -1;

    // Measured even while locked, so a stroke that starts mid-transition still counts.
    this.#accumulate(direction, Math.abs(event.deltaY));

    if (this.#locked) {
      event.preventDefault();
      return;
    }

    // Checked before consuming the event, so a panel with its own overflow still scrolls.
    if (this.#activePanelCanScroll(direction)) return;

    // Consumed whether or not the turn is in range. The component is pinned against a tall
    // spacer whose entire length sits inside the pin, so native scroll here reveals nothing
    // — it only slides the document out from under the pin, taking the in-flow announcement
    // bar above the sticky header off screen, and #isPinned() still holds afterwards so every
    // subsequent event is spent paging instead of unwinding it. Gating this on the turn being
    // in range is what let one scroll at the last panel strand the bar off screen for the rest
    // of the visit; #resyncDocumentScroll recovers a document that drifted some other way.
    event.preventDefault();

    if (this.#scrollSinceTurn < WHEEL_DISTANCE_THRESHOLD) return;

    this.#go(direction);
  };

  #onTouchStart = (/** @type {TouchEvent} */ event) => {
    // Left null for a gesture that began inside an overlay, so an overlay that closes
    // mid-drag can't let #onTouchEnd commit the tail of that gesture as a page swipe.
    this.#touchStartY = this.#shouldReleaseInput(event.target) ? null : (event.touches[0]?.clientY ?? null);
  };

  #onTouchMove = (/** @type {TouchEvent} */ event) => {
    if (this.#touchStartY === null || this.#shouldReleaseInput(event.target) || !this.#isPinned()) return;

    const currentY = event.touches[0]?.clientY ?? this.#touchStartY;
    const delta = this.#touchStartY - currentY;
    if (Math.abs(delta) < TAP_THRESHOLD) return;

    const direction = delta > 0 ? 1 : -1;

    if (this.#activePanelCanScroll(direction)) return;

    // Consumed even when the swipe can't turn a page — see #onWheel.
    event.preventDefault();
  };

  #onTouchEnd = (/** @type {TouchEvent} */ event) => {
    if (this.#touchStartY === null) return;

    const endY = event.changedTouches[0]?.clientY ?? this.#touchStartY;
    const delta = this.#touchStartY - endY;
    this.#touchStartY = null;

    if (this.#shouldReleaseInput(event.target) || !this.#isPinned() || Math.abs(delta) < SWIPE_THRESHOLD) return;

    const direction = delta > 0 ? 1 : -1;
    if (this.#activePanelCanScroll(direction)) return;

    this.#go(direction);
  };

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (this.#shouldReleaseInput(event.target) || !this.#isPinned()) return;

    const target = event.target;
    if (target instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

    if (event.key === 'ArrowDown' || event.key === 'PageDown') {
      if (this.#activePanelCanScroll(1)) return;
      // Consumed even at the last panel, where #go() can't turn — see #onWheel.
      this.#go(1);
      event.preventDefault();
    } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
      if (this.#activePanelCanScroll(-1)) return;
      this.#go(-1);
      event.preventDefault();
    }
  };
}

if (!customElements.get('homepage-scroll-snap')) {
  customElements.define('homepage-scroll-snap', HomepageScrollSnap);
}
